//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import {
  BadgeProps,
  Card,
  CardContent,
  CardHeader,
  darken,
  Fade,
  Grid,
  lighten,
  Tab as MUITab,
  Tabs as MUITabs
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectToggledTitleSearchMode, selectVersionSelections } from '@/redux/selectors/orderPlacementSelectors';
import { TabBadge } from '@/components/_common';
import PolarisIcon from '@/components/_common/PolarisIcon';
import SectionHeading from '@/components/_common/SectionHeading';
import OPSelectionsEmpty from '@/components/OrderPlacement/OPSelectionsEmpty';
import OPOrderStepperButtons from '@/components/OrderPlacement/OPOrderStepperButtons';
import CSVSearch from '@/components/OrderPlacement/OPTitleSearch/CSVSearch/CSVSearch';
import OPVersionSelectionsCSV from './OPVersionSelectionsCSV';
import { TitleConfig, TitleSearchMode } from '@/__configs/config_types';
import OPStep2Breadcrumbs from '@/components/OrderPlacement/OPStep2VersionSelection/OP2Step2Breadcrumbs';
import BulkTitleSearch from '@/components/OrderPlacement/OPStep2VersionSelection/CSVVersionSelection/BulkTitleSearch/BulkTitleSearch';
import { useEffect, useMemo, useState } from 'react';
import { clearBulkErrors, resetBulkSearch, removeBulkError } from '@/redux/slices/bulkSearchSlice';
import BulkSearchResultList from './BulkTitleSearch/BulkSearchResultList';
import { selectBulkTitleErrors } from '@/redux/selectors/bulkSearchSelectors';
import ErrorList from './BulkTitleSearch/ErrorList';
import { selectFilteredActiveTitle } from '@/redux/selectors/titleSelectors';
import { selectActiveTitle } from '@/redux/selectors/titleV2Selectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import OPVersionSelections from '../OPVersionSelections/OPVersionSelections';
import TitleBrowser from '../../OPStep2FileSelection/TitleBrowser/TitleBrowser';

const OPStep2CSVVersionSelection = () => {
  const dispatch = useAppDispatch();
  const isTitleV2 = useConfigValue<TitleConfig['v2']>(['titleConfig', 'v2']);
  const activeTitle = isTitleV2 ? useAppSelector(selectActiveTitle) : useAppSelector(selectFilteredActiveTitle);
  const versionSelections = useAppSelector(selectVersionSelections);
  const bulkTitleErrors = useAppSelector(selectBulkTitleErrors);
  const titleSearchMode = useAppSelector(selectToggledTitleSearchMode);
  const [view, setView] = useState<'selections' | 'errors'>('selections');

  useEffect(() => {
    dispatch(resetBulkSearch());
  }, []);

  const getTabLabel = (label: string, icon: string, color: BadgeProps['color'], count: number) => {
    return (
      <StyledSectionHeading color={color} icon={<PolarisIcon icon={icon} width={22} />}>
        {label}
        {count > 0 && <TabBadge color={color} count={count} />}
      </StyledSectionHeading>
    );
  }

  const onClearAllErrors = () => {
    dispatch(clearBulkErrors());
    setView('selections');
  }

  useEffect(() => {
    if (bulkTitleErrors.length <= 0) {
      setView('selections');
    }
  }, [bulkTitleErrors]);

  const versionSelectionComponent = useMemo(() => {
    if (activeTitle) {
      return <OPVersionSelections />;
    }
    return <OPVersionSelectionsCSV />;
  }, [activeTitle]);

  const titleSearchComponent = useMemo(() => {
    let browser = <BulkSearchResultList />;
    if (activeTitle) {
      browser = <TitleBrowser />;
    }

    return (
      <BulkTitleSearch>
        <OPStep2Breadcrumbs />
        {browser}
      </BulkTitleSearch>
    );
  }, [activeTitle]);

  return (
    <>
      <Grid container spacing={5}>
        {titleSearchMode === TitleSearchMode.BULK_OR_PASTE ? (
          <Grid xs={12} md={7} item position="relative">
            <CSVSearch />
          </Grid>
        ) : titleSearchComponent}
        <Grid xs={12} md={5} item>
          <Fade in timeout={900}>
            <SelectedVersionsCard elevation={0}>
              <SelectedVersionsHeader
                title={
                  versionSelections.length || bulkTitleErrors.length ? (
                    <Tabs data-view={view} value={view} onChange={(event, newValue) => setView(newValue)}>
                      <Tab style={{ display: !versionSelections.length && !bulkTitleErrors.length ? 'none' : '' }} value="selections" label={getTabLabel("Selected Versions", "version_based", "teal", versionSelections.length)} />
                      <Tab style={{ display: !bulkTitleErrors.length ? 'none' : '' }} value="errors" label={getTabLabel("Invalid IDs", "error", "error", bulkTitleErrors.length)} />
                    </Tabs>
                  ) : null
                }
              />
              {view === 'selections' && (
                <CardContent sx={{ pt: 0 }}>
                  {!versionSelections.length ? <OPSelectionsEmpty text="Search for a title to begin adding versions" /> : versionSelectionComponent}
                </CardContent>
              )}
              {view === 'errors' && (
                <CardContent sx={{ pt: 0 }}>
                  {!versionSelections.length && !bulkTitleErrors.length ? <OPSelectionsEmpty text="Search for a title to begin adding versions" /> : (
                    <ErrorList onRemoveError={(id) => dispatch(removeBulkError(id))} onClearAll={onClearAllErrors} list={bulkTitleErrors} />
                  )}
                </CardContent>
              )}
            </SelectedVersionsCard>
          </Fade>
          <Grid container>
            <Grid item ml="auto" mt={5}>
              <OPOrderStepperButtons />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default OPStep2CSVVersionSelection;

const SelectedVersionsCard = styled(Card)`
  background-color: ${({ theme }) => theme.palette.background.paper};
`;

const SelectedVersionsHeader = styled(CardHeader)`
  color: ${({ theme }) => theme.palette.background.paper};
`;

const StyledSectionHeading = styled(SectionHeading)`
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Tabs = styled(MUITabs)`
  .MuiTabs-indicator {
    background-color: ${({ theme }) => theme.palette.teal.light};
  }

  &[data-view="errors"] .MuiTabs-indicator {
    background-color: ${({ theme }) => theme.palette.error.light};
  }
`;

const Tab = styled(MUITab)`
  & {
    padding: ${({ theme }) => theme.spacing(1, 2, 1, 0)};
  }

  .MuiBox-root {
    margin-bottom: ${({ theme }) => theme.spacing(0.5)};
  }

  &[aria-selected="false"] {
    .MuiTypography-root.MuiTypography-h2 {
      color: ${({ theme }) => theme.palette.mode === 'light' ? darken(theme.palette.background.paper, 0.15) : lighten(theme.palette.background.paper, 0.55)};
      text-transform: capitalize;
    }
  }

  .MuiTypography-root.MuiTypography-h2 {
    font-size: 1.07rem;
  }

  &[aria-selected="true"]:first-of-type {
    .MuiTypography-root.MuiTypography-h2 {
      color: ${({ theme }) => theme.palette.teal.light};
      text-transform: capitalize;
    }
  }

  &[aria-selected="true"]:last-of-type {
    .MuiTypography-root.MuiTypography-h2 {
      color: ${({ theme }) => theme.palette.error.light};
      text-transform: capitalize;
    }
  }
`;